import './StoreCartSummary.scss';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { filterCartItemsByStoreOptionSelections } from '../helpers/storeCartOptionSelection.utils';
import {
  getSortedStoreCartDesignGroupEntries,
  getStoreCartDesignGroupKey,
  type IStoreCartDesignGroupShell,
} from '../helpers/storeCartDesignGroups.utils';
import { isStoreCartItemBlockedForCheckout } from '../helpers/storeCartSetup.utils';
import StoreCartSetupButton from '../StoreCartSetupButton/StoreCartSetupButton';
import StoreCartGroupPriceDisplay from '../StoreCartGroupPriceDisplay/StoreCartGroupPriceDisplay';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import { useAppSelector } from '@/store/hooks';
import { useSetupEventStore } from '@/hooks/useSetupEventStore';
import ImageWrapper from '@/components/ImageWrapper';
import { Text } from '@/components/Text/Text';
import useViewport from '@/common/context/ViewPort/useViewPort';

interface IStoreCartSummary {
  showSetupStoreButton?: boolean;
  designGroupShells?: Record<string, IStoreCartDesignGroupShell>;
}

const SCROLLBAR_HIDE_DELAY_MS = 800;

/** This component contains the store cart summary section */
const StoreCartSummary: React.FC<IStoreCartSummary> = ({
  showSetupStoreButton = true,
  designGroupShells = {},
}) => {
  const { watch } = useFormContext();
  const cartDetailsFormArray = watch('cartDetails');
  const screen = useViewport(true);
  const styleClass = `store-cart-summary--${screen}`;
  const optionSelectionsByGroupKey = useAppSelector(
    (state) => state.storeCart.optionSelectionsByGroupKey
  );
  const designGroupShellsFromState = useAppSelector(
    (state) => state.storeCart.designGroupShellsByKey
  );
  const { setupEventStore, isSettingUp } = useSetupEventStore();
  const itemsListRef = useRef<HTMLDivElement | null>(null);
  const scrollHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleItemsListScroll = useCallback(() => {
    const listEl = itemsListRef.current;
    if (!listEl) {
      return;
    }

    listEl.classList.add(`${styleClass}__items-list--scrolling`);

    if (scrollHideTimerRef.current) {
      clearTimeout(scrollHideTimerRef.current);
    }

    scrollHideTimerRef.current = setTimeout(() => {
      listEl.classList.remove(`${styleClass}__items-list--scrolling`);
    }, SCROLLBAR_HIDE_DELAY_MS);
  }, [styleClass]);

  useEffect(
    () => () => {
      if (scrollHideTimerRef.current) {
        clearTimeout(scrollHideTimerRef.current);
      }
    },
    []
  );

  const filteredCartItems = useMemo(() => {
    if (!cartDetailsFormArray?.length) {
      return [];
    }

    return filterCartItemsByStoreOptionSelections(cartDetailsFormArray, optionSelectionsByGroupKey);
  }, [cartDetailsFormArray, optionSelectionsByGroupKey]);

  const handleSetupStore = useCallback(() => {
    setupEventStore(filteredCartItems, {
      ...designGroupShellsFromState,
      ...designGroupShells,
    }).catch(() => {
      /* setup errors are handled inside useSetupEventStore */
    });
  }, [designGroupShells, designGroupShellsFromState, filteredCartItems, setupEventStore]);

  /** Group items by design group */
  const groupedItems = useMemo(() => {
    if (!filteredCartItems.length) return {};

    const groups: Record<string | number, ICartItemDetail[]> = {};
    filteredCartItems.forEach((item: ICartItemDetail) => {
      const groupKey = getStoreCartDesignGroupKey(item);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [filteredCartItems]);

  const sortedDesignGroups = useMemo(() => {
    const shellEntries = getSortedStoreCartDesignGroupEntries(designGroupShells);

    if (shellEntries.length) {
      return shellEntries
        .filter(([groupKey]) => groupedItems[groupKey]?.length)
        .map(([groupKey, shell]) => ({
          groupKey,
          groupLabel: shell.designGroupName,
          items: groupedItems[groupKey],
        }));
    }

    return Object.entries(groupedItems).map(([groupKey, items], groupIndex) => ({
      groupKey,
      groupLabel: `Design ${String.fromCharCode(65 + groupIndex)}`,
      items,
    }));
  }, [designGroupShells, groupedItems]);

  /** This is to render the store cart items grouped by design */
  const renderStoreCartItems = () => {
    if (!filteredCartItems.length) return null;

    return sortedDesignGroups.map(({ groupKey, groupLabel, items }) => (
      <div key={groupKey} className={`${styleClass}__design-group`}>
        <div className={`${styleClass}__design-group__header`}>
          <Text as="span" size={13} color="#8D90AA" weight="regular">
            {groupLabel}
          </Text>
        </div>
        {items.map((item, itemIndex) => (
          <div
            className={`${styleClass}__item-wrapper`}
            key={`${groupKey}-${String(item.id ?? item.designId)}-${itemIndex}`}
          >
            <div className={`${styleClass}__item-wrapper__left`}>
              {item.primaryImage ? (
                <ImageWrapper
                  size="small"
                  imgUrl={item.primaryImage}
                  fallbackSrc="/src/assets/svg/image_error.svg"
                  width={40}
                  height={40}
                  className={`${styleClass}__item-wrapper__image-thumb`}
                />
              ) : (
                <ImageWrapper
                  size="small"
                  imgUrl="/src/assets/svg/image_error.svg"
                  width={40}
                  height={40}
                  className={`${styleClass}__item-wrapper__image-thumb-placeholder`}
                />
              )}
            </div>
            <div className={`${styleClass}__item-wrapper__right`}>
              <div className={`${styleClass}__item-wrapper__right__product-name`}>
                <Text
                  as="span"
                  className={`${styleClass}__item-wrapper__right__product-name__text`}
                  title={item.apparelName}
                >
                  {item.apparelName || ''}
                </Text>
              </div>
              <div className={`${styleClass}__item-wrapper__right__product-details`}>
                <div className={`${styleClass}__item-wrapper__right__color-swatches`}>
                  <div className={`${styleClass}__item-wrapper__right__color-swatch`}>
                    <div
                      className={`${styleClass}__item-wrapper__right__color-swatch__circle`}
                      style={{ backgroundColor: item.apparelHex || '#ccc' }}
                    />
                  </div>
                </div>
                <StoreCartGroupPriceDisplay
                  groupKey={String(groupKey)}
                  price={Number(item.price)}
                  priceClassName={`${styleClass}__item-wrapper__price`}
                  labelClassName={`${styleClass}__item-wrapper__price-label`}
                  shimmerClassName={`${styleClass}__item-wrapper__price-shimmer`}
                  showPerItemLabel
                  isBlockedForCheckout={isStoreCartItemBlockedForCheckout(item)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    ));
  };

  return (
    <div className={styleClass}>
      <div
        ref={itemsListRef}
        className={`${styleClass}__items-list`}
        onScroll={handleItemsListScroll}
      >
        {renderStoreCartItems()}
      </div>
      {showSetupStoreButton && (
        <div className={`${styleClass}__button-container`}>
          <StoreCartSetupButton
            className={`${styleClass}__setup-store-btn`}
            iconClassName={`${styleClass}__setup-store-btn__icon`}
            labelClassName={`${styleClass}__setup-store-btn__label`}
            onClick={handleSetupStore}
            isLoading={isSettingUp}
          />
          <div className={`${styleClass}__description`}>
            <Text as="p" size={12} color="#8D90AA" weight="bold">
              Customize your branding, pricing, and shipping.
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

StoreCartSummary.displayName = 'StoreCartSummary';

export default memo(StoreCartSummary);
